import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';
import { ACTIVITY_TYPES, getActivityTypeConfig } from '../components/crm/ActivityTypeSelector';

export default function useObjectRecordDetail(objectDefinitionIdOverride) {
  const params = useParams();
  const objectDefinitionId = objectDefinitionIdOverride || params.objectDefinitionId;
  const recordId = params.recordId;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  usePostHog();

  const [record, setRecord] = useState(null);
  const [resolvedData, setResolvedData] = useState({});
  const [objectDefinition, setObjectDefinition] = useState(null);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Activity state
  const [newNote, setNewNote] = useState('');
  const [activityType, setActivityType] = useState('note');
  const [editingActivityId, setEditingActivityId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editActivityType, setEditActivityType] = useState('note');

  // Dynamic relationships (replaces hardcoded employment/deal state)
  const [relationships, setRelationships] = useState([]);

  // Org members (for owner fields)
  const [orgMembers, setOrgMembers] = useState([]);

  const basePath = `/crm/objects/${objectDefinitionId}/records/${recordId}`;
  const objectSlug = params.objectSlug;
  const listPath = objectSlug ? `/data/${objectSlug}` : `/crm/objects/${objectDefinitionId}`;

  // --- Fetch record ---
  const fetchRecord = useCallback(async () => {
    try {
      const response = await apiRequest(basePath);
      if (response.ok) {
        const result = await response.json();
        const flattened = {
          id: result.id,
          object_definition_id: result.object_definition_id,
          organization_id: result.organization_id,
          created_at: result.created_at,
          updated_at: result.updated_at,
          ...result.data,
          activities: result.activities || [],
        };
        setRecord(flattened);
        setResolvedData(result.resolved_data || {});
        setObjectDefinition(result.object_definition);
        setFieldDefinitions(result.field_definitions || []);
        setRelationships(result.relationships || []);
      } else {
        showError('Record not found');
        navigate(listPath);
      }
    } catch (error) {
      console.error('Error fetching record:', error);
      navigate(listPath);
    } finally {
      setLoading(false);
    }
  }, [basePath, listPath, showError, navigate]);

  const fetchOrgMembers = useCallback(async () => {
    try {
      const response = await apiRequest('/organizations/current/members');
      if (response.ok) {
        const data = await response.json();
        setOrgMembers(data);
      }
    } catch (error) {
      console.error('Error fetching org members:', error);
    }
  }, []);

  useEffect(() => {
    fetchRecord();
    fetchOrgMembers();
  }, [fetchRecord, fetchOrgMembers]);

  // --- Field updates ---
  const updateFields = async (updates) => {
    if (!record) {
      showError('Record data not loaded');
      return false;
    }
    const previousValues = {};
    for (const key of Object.keys(updates)) {
      previousValues[key] = record[key];
    }
    setRecord((prev) => ({ ...prev, ...updates }));
    try {
      const response = await apiRequest(basePath, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (response.ok) {
        const result = await response.json();
        setRecord((prev) => ({ ...prev, ...result.data }));
        return true;
      } else {
        setRecord((prev) => ({ ...prev, ...previousValues }));
        showError('Failed to update field');
        return false;
      }
    } catch (error) {
      setRecord((prev) => ({ ...prev, ...previousValues }));
      showError('Failed to update field');
      return false;
    }
  };

  const handleFieldSave = async (fieldName, newValue) => {
    return updateFields({ [fieldName]: newValue });
  };

  // --- Delete record ---
  const deleteRecord = async () => {
    const typeName = objectDefinition?.name || 'Record';
    if (!window.confirm(`Are you sure you want to delete this ${typeName.toLowerCase()}?`)) return;
    try {
      const response = await apiRequest(basePath, { method: 'DELETE' });
      if (response.ok) {
        showSuccess(`${typeName} deleted`);
        navigate(listPath);
      }
    } catch (error) {
      showError(`Failed to delete ${typeName.toLowerCase()}`);
    }
  };

  // --- Activities ---
  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      const response = await apiRequest('/crm/activities/', {
        method: 'POST',
        body: JSON.stringify({
          content: newNote,
          object_record_ids: [recordId],
          activity_type: activityType,
        }),
      });
      if (response.ok) {
        setNewNote('');
        setActivityType('note');
        fetchRecord();
        const typeConfig = getActivityTypeConfig(activityType);
        showSuccess(`${typeConfig.label} added`);
      }
    } catch (error) {
      showError('Failed to add activity');
    }
  };

  const deleteActivity = async (activityId) => {
    try {
      await apiRequest(`/crm/activities/${activityId}`, { method: 'DELETE' });
      fetchRecord();
    } catch (error) {
      showError('Failed to delete note');
    }
  };

  const startEditActivity = (activity) => {
    setEditingActivityId(activity.id);
    setEditContent(activity.content || '');
    setEditActivityType(activity.activity_type);
  };

  const cancelEditActivity = () => {
    setEditingActivityId(null);
    setEditContent('');
    setEditActivityType('note');
  };

  const updateActivity = async (activityId) => {
    try {
      const response = await apiRequest(`/crm/activities/${activityId}`, {
        method: 'PUT',
        body: JSON.stringify({
          content: editContent,
          activity_type: editActivityType,
        }),
      });
      if (response.ok) {
        cancelEditActivity();
        fetchRecord();
        showSuccess('Activity updated');
      }
    } catch (error) {
      showError('Failed to update activity');
    }
  };

  // --- Generic Relationship Operations ---
  const fetchRelatedRecords = useCallback(async (relationshipId) => {
    try {
      const response = await apiRequest(
        `/crm/objects/${objectDefinitionId}/records/${recordId}/related/${relationshipId}`
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error('Error fetching related records:', error);
    }
    return { items: [], total: 0, metadata_fields: [] };
  }, [objectDefinitionId, recordId]);

  const createRecordLink = async (relationshipId, sourceRecordId, targetRecordId, metadata = {}) => {
    try {
      const response = await apiRequest('/crm/record-links', {
        method: 'POST',
        body: JSON.stringify({
          relationship_id: relationshipId,
          source_record_id: sourceRecordId,
          target_record_id: targetRecordId,
          metadata,
        }),
      });
      if (response.ok) {
        fetchRecord();
        showSuccess('Record linked');
        return true;
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.detail || 'Failed to link record');
        return false;
      }
    } catch (error) {
      showError('Failed to link record');
      return false;
    }
  };

  const updateRecordLink = async (linkId, metadata) => {
    try {
      const response = await apiRequest(`/crm/record-links/${linkId}`, {
        method: 'PUT',
        body: JSON.stringify({ metadata }),
      });
      if (response.ok) {
        fetchRecord();
        showSuccess('Link updated');
        return true;
      } else {
        showError('Failed to update link');
        return false;
      }
    } catch (error) {
      showError('Failed to update link');
      return false;
    }
  };

  const deleteRecordLink = async (linkId) => {
    try {
      await apiRequest(`/crm/record-links/${linkId}`, { method: 'DELETE' });
      fetchRecord();
      showSuccess('Link removed');
      return true;
    } catch (error) {
      showError('Failed to remove link');
      return false;
    }
  };

  // --- Derived data ---
  const ownerOptions = orgMembers.map((m) => ({
    value: m.id,
    label: m.full_name || m.username,
  }));

  const ownerDisplay = record?.owner_name || (ownerOptions.find((o) => o.value === record?.owner_id)?.label) || null;

  return {
    // Core data
    record,
    resolvedData,
    objectDefinition,
    fieldDefinitions,
    loading,
    // Relationships
    relationships,
    fetchRelatedRecords,
    createRecordLink,
    updateRecordLink,
    deleteRecordLink,
    // Derived
    ownerOptions,
    ownerDisplay,
    orgMembers,
    // Constants
    ACTIVITY_TYPES,
    getActivityTypeConfig,
    // Activity
    newNote, setNewNote,
    activityType, setActivityType,
    editingActivityId,
    editContent, setEditContent,
    editActivityType, setEditActivityType,
    addNote, deleteActivity,
    startEditActivity, cancelEditActivity, updateActivity,
    fetchRecord,
    // Actions
    handleFieldSave,
    updateFields,
    deleteRecord,
    navigate,
    // IDs for navigation
    objectDefinitionId,
    recordId,
    listPath,
  };
}
